import type { DiagramEdge, DiagramNode, ViewportState } from './types';
import { querySpatialEdgeIndex, querySpatialNodeIndex, type SpatialEdgeIndex, type SpatialNodeIndex } from './spatialIndex';

export interface RenderSubset {
  nodeIds: Set<string>;
  edgeIds: Set<string>;
  culled: boolean;
}

interface Options {
  nodes: DiagramNode[];
  edges: DiagramEdge[];
  viewport: ViewportState;
  width: number;
  height: number;
  selectedNodeIds?: string[];
  selectedEdgeIds?: string[];
  threshold?: number;
  marginPx?: number;
  nodeIndex?: SpatialNodeIndex;
  edgeIndex?: SpatialEdgeIndex;
}

function intersects(left: number, top: number, right: number, bottom: number, viewLeft: number, viewTop: number, viewRight: number, viewBottom: number): boolean {
  return left <= viewRight && right >= viewLeft && top <= viewBottom && bottom >= viewTop;
}

export function computeRenderSubset({ nodes, edges, viewport, width, height, selectedNodeIds = [], selectedEdgeIds = [], threshold = 350, marginPx = 420, nodeIndex, edgeIndex }: Options): RenderSubset {
  const visible = nodes.filter((node) => !node.hidden);
  const visibleIds = new Set(visible.map((node) => node.id));
  const validEdges = edges.filter((edge) => visibleIds.has(edge.sourceId) && visibleIds.has(edge.targetId));
  if (visible.length <= threshold || width <= 0 || height <= 0) {
    return { nodeIds: visibleIds, edgeIds: new Set(validEdges.map((edge) => edge.id)), culled: false };
  }

  const zoom = Math.max(0.2, viewport.zoom || 1);
  const margin = marginPx / zoom;
  const viewLeft = -viewport.panX / zoom - margin;
  const viewTop = -viewport.panY / zoom - margin;
  const viewRight = (width - viewport.panX) / zoom + margin;
  const viewBottom = (height - viewport.panY) / zoom + margin;
  const forcedNodes = new Set(selectedNodeIds);
  const forcedEdges = new Set(selectedEdgeIds);
  const nodeIds = new Set<string>();
  const nodeById = new Map(visible.map((node) => [node.id, node]));

  const candidates = nodeIndex && nodeIndex.visibleCount === visible.length ? querySpatialNodeIndex(nodeIndex, viewLeft, viewTop, viewRight, viewBottom) : visible;
  for (const node of candidates) {
    if (forcedNodes.has(node.id) || intersects(node.x, node.y, node.x + node.width, node.y + node.height, viewLeft, viewTop, viewRight, viewBottom)) nodeIds.add(node.id);
  }
  for (const id of forcedNodes) if (visibleIds.has(id)) nodeIds.add(id);

  const viewportNodeIds = new Set(nodeIds);
  const edgeIds = new Set<string>();
  const indexedEdges = edgeIndex && edgeIndex.visibleCount === validEdges.length
    ? querySpatialEdgeIndex(edgeIndex, viewLeft, viewTop, viewRight, viewBottom)
    : validEdges;
  const candidateEdges = new Map(indexedEdges.map((edge) => [edge.id, edge]));
  for (const forcedId of forcedEdges) {
    const forced = edgeIndex?.edgeById.get(forcedId) ?? validEdges.find((edge) => edge.id === forcedId);
    if (forced) candidateEdges.set(forced.id, forced);
  }
  // Edges attached to a visible node must also be considered even if a manual route temporarily sits outside the indexed cells.
  for (const edge of validEdges) if (viewportNodeIds.has(edge.sourceId) || viewportNodeIds.has(edge.targetId)) candidateEdges.set(edge.id, edge);
  for (const edge of candidateEdges.values()) {
    const source = nodeById.get(edge.sourceId)!;
    const target = nodeById.get(edge.targetId)!;
    const points = [
      { x: source.x, y: source.y }, { x: source.x + source.width, y: source.y + source.height },
      { x: target.x, y: target.y }, { x: target.x + target.width, y: target.y + target.height },
      ...(edge.waypoints ?? []),
    ];
    const xs = points.map((point) => point.x), ys = points.map((point) => point.y);
    const minX = Math.min(...xs), minY = Math.min(...ys), maxX = Math.max(...xs), maxY = Math.max(...ys);
    if (forcedEdges.has(edge.id) || viewportNodeIds.has(source.id) || viewportNodeIds.has(target.id) || intersects(minX, minY, maxX, maxY, viewLeft, viewTop, viewRight, viewBottom)) {
      edgeIds.add(edge.id);
      // Endpoints are required to calculate anchors/routes even when they sit just outside the viewport.
      nodeIds.add(source.id);
      nodeIds.add(target.id);
    }
  }

  return { nodeIds, edgeIds, culled: nodeIds.size < visible.length || edgeIds.size < validEdges.length };
}
